import React, { useState, useMemo } from 'react';
import { Calculator, ShieldAlert, AlertCircle, CheckCircle2 } from 'lucide-react';
import { Card } from '../ui';
import { Input } from '../ui';

export function UnionStrikeFundCalculator() {
  const [totalMembers, setTotalMembers] = useState<number>(1200);
  const [strikePay, setStrikePay] = useState<number>(350); // Weekly pay per member
  const [currentFund, setCurrentFund] = useState<number>(1500000);
  const [expectedDuration, setExpectedDuration] = useState<number>(6); // weeks

  const model = useMemo(() => {
    const weeklyCost = totalMembers * strikePay;
    const totalStrikeCost = weeklyCost * expectedDuration;
    
    const balanceAfterStrike = currentFund - totalStrikeCost;
    const isSolvent = balanceAfterStrike >= 0;
    
    // Calculate required special assessment if deficit exists
    const deficit = isSolvent ? 0 : Math.abs(balanceAfterStrike);
    const requiredAssessmentPerMember = deficit > 0 ? deficit / totalMembers : 0;
    
    // Calculate max weeks fund can survive
    const maxWeeks = weeklyCost > 0 ? Math.floor(currentFund / weeklyCost) : 0;

    return {
      weeklyCost,
      totalStrikeCost,
      balanceAfterStrike,
      isSolvent,
      deficit,
      requiredAssessmentPerMember,
      maxWeeks
    };
  }, [totalMembers, strikePay, currentFund, expectedDuration]);

  const formatCurrency = (val: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(val);

  return (
    <div className="max-w-5xl mx-auto space-y-8 p-4">
      <div className="bg-gradient-to-r from-red-700 to-rose-900 rounded-2xl p-8 text-white shadow-xl flex flex-col md:flex-row items-center justify-between gap-6">
        <div>
          <h2 className="text-2xl font-black flex items-center mb-2">
            <ShieldAlert className="w-6 h-6 mr-3 text-red-300" />
            Strike Fund Solvency Calculator
          </h2>
          <p className="text-red-100 max-w-xl">
            Model the financial impact of a prolonged strike. Determine exactly how long your current war chest will last, and calculate special assessments needed to stay solvent.
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        
        {/* Inputs */}
        <Card className="p-6 space-y-6 border-slate-200 dark:border-slate-800 shadow-xl bg-white dark:bg-slate-900">
          <div>
            <label className="text-sm font-semibold text-slate-500 uppercase block mb-2">Current Fund Balance ($)</label>
            <Input 
              type="number" 
              value={currentFund} 
              onChange={(e) => setCurrentFund(Number(e.target.value))}
              className="text-lg font-bold"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-semibold text-slate-500 uppercase block mb-2">Total Members</label>
              <Input 
                type="number" 
                value={totalMembers} 
                onChange={(e) => setTotalMembers(Number(e.target.value))}
              />
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-500 uppercase block mb-2">Weekly Pay ($)</label>
              <Input 
                type="number" 
                value={strikePay} 
                onChange={(e) => setStrikePay(Number(e.target.value))}
              />
            </div>
          </div>

          <div className="pt-4 border-t border-slate-100 dark:border-slate-800">
            <label className="text-sm font-semibold text-slate-500 uppercase flex justify-between mb-2">
              <span>Expected Duration</span>
              <span className="font-bold text-slate-900 dark:text-white">{expectedDuration} Weeks</span>
            </label>
            <input 
              type="range" 
              min="1" 
              max="24" 
              value={expectedDuration} 
              onChange={(e) => setExpectedDuration(Number(e.target.value))}
              className="w-full accent-red-600"
            />
            <div className="flex justify-between text-xs text-slate-400 mt-2">
              <span>1 Week</span>
              <span>6 Months</span>
            </div>
          </div>
        </Card>

        {/* Results */}
        <div className="space-y-6">
          <Card className={`p-6 border-slate-200 dark:border-slate-800 shadow-xl text-white ${model.isSolvent ? 'bg-slate-900' : 'bg-red-900'}`}>
            <div className="flex items-start justify-between mb-4">
              <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider">Projected Post-Strike Balance</h3>
              {model.isSolvent ? <CheckCircle2 className="w-6 h-6 text-emerald-500" /> : <AlertCircle className="w-6 h-6 text-red-400" />}
            </div>
            
            <div className={`text-5xl font-black tracking-tight ${model.isSolvent ? 'text-emerald-400' : 'text-red-400'}`}>
              {formatCurrency(model.balanceAfterStrike)}
            </div>

            {!model.isSolvent && (
              <div className="mt-4 p-4 bg-black/20 rounded-lg border border-red-500/30">
                <div className="text-sm text-red-200 uppercase font-bold tracking-wider mb-1">Required Special Assessment</div>
                <div className="text-2xl font-bold text-white">{formatCurrency(model.requiredAssessmentPerMember)} <span className="text-sm font-normal text-red-200">/ member</span></div>
                <div className="text-xs text-red-300 mt-1">To cover the {formatCurrency(model.deficit)} deficit</div>
              </div>
            )}
          </Card>

          <Card className="p-0 border-slate-200 dark:border-slate-800 shadow-xl bg-white dark:bg-slate-900 overflow-hidden">
            <div className="bg-slate-50 dark:bg-slate-800/50 p-4 border-b border-slate-100 dark:border-slate-800">
              <h3 className="font-bold">Fund Burn Rate</h3>
            </div>
            
            <div className="divide-y divide-slate-100 dark:divide-slate-800">
              <div className="p-4 flex items-center justify-between">
                <div>
                  <div className="font-bold text-slate-900 dark:text-white">Weekly Burn Rate</div>
                  <div className="text-sm text-slate-500">Total cost per week of strike</div>
                </div>
                <div className="text-xl font-bold text-red-600 dark:text-red-400">
                  {formatCurrency(model.weeklyCost)}
                </div>
              </div>
              
              <div className="p-4 flex items-center justify-between">
                <div>
                  <div className="font-bold text-slate-900 dark:text-white">Total Strike Cost</div>
                  <div className="text-sm text-slate-500">Over {expectedDuration} weeks</div>
                </div>
                <div className="text-xl font-bold">
                  {formatCurrency(model.totalStrikeCost)}
                </div>
              </div>
              
              <div className="p-4 flex items-center justify-between bg-slate-50 dark:bg-slate-800/50">
                <div>
                  <div className="font-bold text-slate-900 dark:text-white">Maximum Duration</div>
                  <div className="text-sm text-slate-500">Before fund is completely empty</div>
                </div>
                <div className="text-xl font-bold text-slate-900 dark:text-white">
                  {model.maxWeeks} Weeks
                </div>
              </div>
            </div>
          </Card>
        </div>

      </div>
    </div>
  );
}
